import api from "@/lib/axiosConfig";
import { Ensemble } from "@/lib/types";
import {
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from "@tanstack/react-query";

type GetArtworkSetsOptions = {
  search?: string;
  withArtworksIds?: string[];
  visibility?: "private" | "shared";
};

const getArtworkSets = async (
  query?: GetArtworkSetsOptions,
): Promise<Ensemble[]> => {
  return api
    .get<Ensemble[]>("back/api/ensemble", {
      params: {
        ...query,
        ensemblesDescriptionType: "temporaire",
      },
    })
    .then(({ data }) =>
      data.map(({ ensemblesDescriptionDateCreationEnsemble, ...other }) => ({
        ...other,
        ensemblesDescriptionDateCreationEnsemble:
          ensemblesDescriptionDateCreationEnsemble
            ? new Date(ensemblesDescriptionDateCreationEnsemble)
            : undefined,
      })),
    );
};

export const useGetArtworkSets = (
  query?: GetArtworkSetsOptions,
  options?: Omit<UseQueryOptions<Ensemble[]>, "queryKey" | "queryFn">,
) =>
  useQuery<Ensemble[]>({
    ...options,
    queryKey: ["ensembles", query],
    queryFn: () => getArtworkSets(query),
  });

export const getOneArtworkSet = async (id: string): Promise<Ensemble> => {
  return api
    .get<Ensemble>(`back/api/ensemble/${id}`)
    .then(
      ({ data: { ensemblesDescriptionDateCreationEnsemble, ...other } }) => ({
        ...other,
        ensemblesDescriptionDateCreationEnsemble:
          ensemblesDescriptionDateCreationEnsemble
            ? new Date(ensemblesDescriptionDateCreationEnsemble)
            : undefined,
      }),
    );
};

export const useGetOneArtworkSet = (
  id: string,
  options?: Omit<UseQueryOptions<Ensemble>, "queryKey" | "queryFn">,
) =>
  useQuery<Ensemble>({
    ...options,
    queryKey: ["ensembles", id],
    queryFn: () => getOneArtworkSet(id),
  });

type CreateEmptySetParams = {
  ensemblesDescriptionNomEnsemble: string;
  ensemblesDescriptionNotesEnsemble?: string;
  visibility: string;
};

const createEmptyTemporarySet = async (data: CreateEmptySetParams) => {
  return await api
    .post<Ensemble>("back/api/ensemble", {
      ...data,
      ensemblesDescriptionType: "temporaire",
    })
    .then((res) => res.data);
};

export const useCreateEmptyTemporarySet = (
  options?: Omit<
    UseMutationOptions<Ensemble, Error, CreateEmptySetParams>,
    "mutationFn"
  >,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: createEmptyTemporarySet,
    onSuccess: (data, variables, context) => {
      options?.onSuccess?.(data, variables, context);
      queryClient.invalidateQueries({ queryKey: ["ensembles"] });
    },
  });
};

const updateTemporarySet = async ({
  id,
  data,
}: {
  id: string;
  data: CreateEmptySetParams;
}) => {
  return await api
    .patch<Ensemble>(`back/api/ensemble/${id}`, data)
    .then((res) => res.data);
};

export const useUpdateTemporarySet = (
  options?: Omit<
    UseMutationOptions<
      Ensemble,
      Error,
      { id: string; data: CreateEmptySetParams }
    >,
    "mutationFn"
  >,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: updateTemporarySet,
    onSuccess: (data, variables, context) => {
      options?.onSuccess?.(data, variables, context);
      queryClient.invalidateQueries({ queryKey: ["ensembles"] });
    },
  });
};

type CreateSetWithArtworksParams = CreateEmptySetParams & {
  artworksIds: string[];
};

export const useCreateTemporarySetWithArtworks = (
  options?: Omit<
    UseMutationOptions<Ensemble, Error, CreateSetWithArtworksParams>,
    "mutationFn"
  >,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: async (data: CreateSetWithArtworksParams) => {
      const { artworksIds, ...setParams } = data;
      const set = await createEmptyTemporarySet(setParams);
      if (data.artworksIds.length === 0) return set;
      await addArtworksToEnsembles({
        ensembleIds: [set.id],
        artworkIds: artworksIds,
      });
      return set;
    },
    onSuccess: (data, variables, context) => {
      options?.onSuccess?.(data, variables, context);
      queryClient.invalidateQueries({ queryKey: ["ensembles"] });
    },
  });
};

type ModifySetsArtworksOptions = {
  ensembleIds: string[];
  artworkIds: string[];
};

const addArtworksToEnsembles = async ({
  artworkIds,
  ensembleIds,
}: ModifySetsArtworksOptions) => {
  await api.patch<void>(`back/api/ensemble/add-artworks`, {
    ensembleIds,
    artworkIds,
  });
};

export const useAddArtworksToEnsembles = (
  options?: Omit<
    UseMutationOptions<void, Error, ModifySetsArtworksOptions>,
    "mutationFn"
  >,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: addArtworksToEnsembles,
    onSuccess: (data, variables, context) => {
      options?.onSuccess?.(data, variables, context);
      queryClient.invalidateQueries({ queryKey: ["ensembles"] });
      queryClient.invalidateQueries({ queryKey: ["artworksDashboard"] });
      queryClient.invalidateQueries({ queryKey: ["countByStatus", "artwork"] });
    },
  });
};

const removeArtworksFromEnsembles = async ({
  artworkIds,
  ensembleIds,
}: ModifySetsArtworksOptions) => {
  await api.patch<void>(`back/api/ensemble/remove-artworks`, {
    ensembleIds,
    artworkIds,
  });
};

export const useRemoveArtworksFromEnsembles = (
  options?: Omit<
    UseMutationOptions<void, Error, ModifySetsArtworksOptions>,
    "mutationFn"
  >,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: removeArtworksFromEnsembles,
    onSuccess: (data, variables, context) => {
      options?.onSuccess?.(data, variables, context);
      queryClient.invalidateQueries({ queryKey: ["ensembles"] });
      queryClient.invalidateQueries({ queryKey: ["artworksDashboard"] });
      queryClient.invalidateQueries({ queryKey: ["countByStatus", "artwork"] });
    },
  });
};

const deleteEnsembles = async (id: string) => {
  await api.patch<void>(`back/api/ensemble/delete`, {
    where: {
      id,
    },
  });
};

export const useDeleteEnsembles = (
  options?: Omit<UseMutationOptions<void, Error, string>, "mutationFn">,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: deleteEnsembles,
    onSuccess: (data, variables, context) => {
      options?.onSuccess?.(data, variables, context);
      queryClient.invalidateQueries({ queryKey: ["ensembles"] });
    },
  });
};
